import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const { t } = useLang();
  const [status, setStatus] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  const { clear } = useCart();
  const attemptRef = useRef(0);
  const sessionId = sp.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setOrderId(data.order_id);
          clear();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
        if (attemptRef.current >= 10) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto text-center py-24 px-6" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="w-16 h-16 text-[#1A3322] mx-auto animate-spin"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.verify")}</h1>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-20 h-20 text-[#1A3322] mx-auto" data-testid="payment-success-icon"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.confirmed")}</h1>
          <p className="text-[#4B5563] mt-4">{t("pay.confirmed_desc")}</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/commandes"><Button className="pill-btn bg-[#1A3322] rounded-full" data-testid="view-orders-btn">{t("pay.my_orders")}</Button></Link>
            <Link to="/"><Button variant="outline" className="pill-btn rounded-full">{t("pay.home")}</Button></Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-20 h-20 text-[#C85A32] mx-auto"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.pending_title")}</h1>
          <p className="text-[#4B5563] mt-4">{t("pay.pending_desc")}</p>
          <Link to="/commandes"><Button className="mt-6 pill-btn bg-[#1A3322] rounded-full">{t("pay.my_orders")}</Button></Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <div className="max-w-lg mx-auto text-center py-24 px-6" data-testid="payment-cancel-page">
      <XCircle className="w-20 h-20 text-[#C85A32] mx-auto"/>
      <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.cancelled_title")}</h1>
      <p className="text-[#4B5563] mt-4">{t("pay.cancelled_desc")}</p>
      <Link to="/panier"><Button className="mt-6 pill-btn bg-[#1A3322] rounded-full">{t("pay.back_cart")}</Button></Link>
    </div>
  );
}

export function PawaPayReturn() {
  const [sp] = useSearchParams();
  const { t } = useLang();
  const [status, setStatus] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  const { clear } = useCart();
  const attemptRef = useRef(0);
  const depositId = sp.get("deposit_id");

  useEffect(() => {
    if (!depositId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptRef.current += 1;
      try {
        const { data } = await api.get(`/pawapay/status/${depositId}`);
        if (data.payment_status === "paid") {
          setStatus("paid"); setOrderId(data.order_id); clear(); return;
        }
        if (data.status === "failed" || data.payment_status === "failed") {
          setStatus("failed"); return;
        }
        if (attemptRef.current >= 15) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [depositId]);

  return (
    <div className="max-w-lg mx-auto text-center py-24 px-6" data-testid="pawapay-return-page">
      {status === "checking" && (
        <>
          <Loader2 className="w-16 h-16 text-[#1A3322] mx-auto animate-spin"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.verify_mm")}</h1>
          <p className="text-[#4B5563] mt-4">{t("pay.verify_mm_desc")}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-20 h-20 text-[#1A3322] mx-auto" data-testid="pawapay-success-icon"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.confirmed")}</h1>
          <p className="text-[#4B5563] mt-4">{t("pay.pawapay_confirmed_desc")}</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/commandes"><Button className="pill-btn bg-[#1A3322] rounded-full">{t("pay.my_orders")}</Button></Link>
            <Link to="/"><Button variant="outline" className="pill-btn rounded-full">{t("pay.home")}</Button></Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-20 h-20 text-[#C85A32] mx-auto"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.pending_title")}</h1>
          <p className="text-[#4B5563] mt-4">{t("pay.pending_desc_pp")}</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/commandes"><Button className="pill-btn bg-[#1A3322] rounded-full">{t("pay.my_orders")}</Button></Link>
            <Link to="/panier"><Button variant="outline" className="pill-btn rounded-full">{t("pay.back_cart")}</Button></Link>
          </div>
        </>
      )}
    </div>
  );
}


export function FlutterwaveReturn() {
  const [sp] = useSearchParams();
  const { t } = useLang();
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();
  const attemptRef = useRef(0);
  const txRef = sp.get("tx_ref");

  useEffect(() => {
    if (!txRef) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptRef.current += 1;
      try {
        const { data } = await api.get(`/flutterwave/status/${txRef}`);
        if (data.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (data.payment_status === "failed") { setStatus("failed"); return; }
        if (attemptRef.current >= 15) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [txRef]);

  return (
    <div className="max-w-lg mx-auto text-center py-24 px-6" data-testid="flutterwave-return-page">
      {status === "checking" && (
        <>
          <Loader2 className="w-16 h-16 text-[#1A3322] mx-auto animate-spin"/>
          <h1 className="heading font-extrabold text-3xl mt-6">Vérification du paiement…</h1>
          <p className="text-[#4B5563] mt-4">Nous confirmons votre transaction avec Flutterwave. Merci de patienter quelques secondes.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-20 h-20 text-[#1A3322] mx-auto" data-testid="flutterwave-success-icon"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.confirmed")}</h1>
          <p className="text-[#4B5563] mt-4">{t("pay.confirmed_desc")}</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/commandes"><Button className="pill-btn bg-[#1A3322] rounded-full">{t("pay.my_orders")}</Button></Link>
            <Link to="/"><Button variant="outline" className="pill-btn rounded-full">{t("pay.home")}</Button></Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-20 h-20 text-[#C85A32] mx-auto"/>
          <h1 className="heading font-extrabold text-3xl mt-6">{t("pay.pending_title")}</h1>
          <p className="text-[#4B5563] mt-4">Votre paiement est en cours de traitement. Consultez vos commandes pour voir le statut à jour.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/commandes"><Button className="pill-btn bg-[#1A3322] rounded-full">{t("pay.my_orders")}</Button></Link>
            <Link to="/panier"><Button variant="outline" className="pill-btn rounded-full">{t("pay.back_cart")}</Button></Link>
          </div>
        </>
      )}
    </div>
  );
}
